import { TOR_STATUS_LABELS, type TorStatus } from "@convenor/shared";

/** The ToR status as a tag. The text carries the meaning, never the colour alone. */
// The ToR states on the system's tag colours; "withdrawn" is a quiet tag (the word says it).
const TOR_TONE: Record<TorStatus, string> = {
  draft: "tag--neutral",
  submitted: "tag--warning",
  revision: "tag--danger",
  signed_off: "tag--info",
  approved: "tag--success",
  withdrawn: "tag--quiet",
};

export function StatusTag({ status, version }: { status: TorStatus; version?: number }) {
  return (
    <span className={`tag ${TOR_TONE[status]}`}>
      {TOR_STATUS_LABELS[status]}
      {version !== undefined && version > 1 ? ` · v${version}` : ""}
    </span>
  );
}

/** A neutral tag for other short labels ("Changed", "Closed", a count). */
export function Tag({ children, tone = "neutral" }: { children: React.ReactNode; tone?: "neutral" | "info" | "warning" | "success" | "danger" }) {
  return <span className={`tag tag--${tone}`}>{children}</span>;
}

export type Tone = "neutral" | "info" | "warning" | "success" | "danger";

/** A status pill whose tone comes from a lookup (areas/<wp>/status-tones.ts). */
export function StatusPill({ label, tone }: { label: string; tone: Tone }) {
  return <Tag tone={tone}>{label}</Tag>;
}

/** "From the record": the value was written by another module. */
export function RecordTag({ children = "From the record" }: { children?: React.ReactNode }) {
  return <span className="tag tag--quiet">{children}</span>;
}

/** A count (tab counts). `label` is read instead of the bare number. */
export function Badge({ count, label }: { count: number; label?: string }) {
  return (
    <span className="tabs__count" aria-label={label}>
      {count}
    </span>
  );
}

/**
 * Calendar chip: the system's Chip, one look. `tone` (events, missions, meetings, report
 * deadlines) is kept for callers; the entry's words say what it is.
 */
export function Chip({ children }: { children: React.ReactNode; tone?: Tone }) {
  return <span className="chip">{children}</span>;
}
